import ApartmentIcon from "@mui/icons-material/Apartment";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import NoteIcon from "@mui/icons-material/Note";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../common/button/commonButton";
import MessageDialog from "../../common/dialog/commonDialogBox";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { styles } from "../../commonStyle/formStyles";
import {
  fetchAdmission,
  fetchAllAdmission
} from "../../redux/admissions/admissionsActions";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchDepartments } from "../../redux/department/departmentAction";
import { fetchAllFloors, fetchFloors } from "../../redux/floor/floorAction";
import {
  addTransferPatient,
  resetCurrentTransferPatient,
} from "../../redux/TransferPatient/transferPatientAction";
import { fetchAllWards, fetchWards } from "../../redux/ward/wardAction";

const PatientTransferForm = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const [dialogProps, setDialogProps] = useState({
    title: "",
    message: "",
    type: "success",
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      admission_id: "",
      patient_id: "",
      old_block_id: "",
      old_floor_id: "",
      old_ward_id: "",
      new_block_id: "",
      new_floor_id: "",
      new_ward_id: "",
      reason: "",
    },
  });

  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const floorAllList = useSelector(
    (state) => state?.floor?.fetchAllFloors || {}
  );
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const wardAllList = useSelector((state) => state?.ward?.fetchAllWards || {});
  const loginValues = useSelector((state) => state.values?.loginValues || []);
  const userInfo = loginValues[0]?.user || {};
  const Id = userInfo.staff_id;
  const transferPatientState = useSelector(
    (state) => state?.transferPatient?.addTransferPatients || {}
  );

  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAllAdmission || {}
  );

  const watchBlock = watch("new_block_id");
  const watchFloor = watch("new_floor_id");
  const watchWard = watch("new_ward_id");
  const watchAdmission = watch("admission_id");

  // Get the selected admission data
  const selectedAdmission = watchAdmission
    ? admissionsList?.admissions?.data?.find(
        (admission) => admission.id === watchAdmission
      )
    : null;

  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem("block", watchBlock);
    }
    if (watchWard) {
      localStorage.setItem("ward", watchWard);
    }
    if (watchFloor) {
      localStorage.setItem("floor", watchFloor);
    }
  }, [watchBlock, watchWard, watchFloor]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchAllAdmission());
      await dispatch(fetchDepartments());
      await dispatch(fetchBlocks());
      await dispatch(fetchAllWards());
      await dispatch(fetchAllFloors());
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (watchAdmission) {
      dispatch(fetchAdmission(watchAdmission));
    }
  }, [watchAdmission, dispatch]);

  useEffect(() => {
    if (selectedAdmission) {
      setValue("old_block_id", selectedAdmission.block_id || "");
      setValue("old_floor_id", selectedAdmission.floor_id || "");
      setValue("old_ward_id", selectedAdmission.ward_id || "");
      setValue("patient_id", selectedAdmission.patient_id || "");
    }
  }, [selectedAdmission, setValue]);

  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchFloors(watchBlock));
      setValue("new_floor_id", "");
      setValue("new_ward_id", "");
    }
  }, [watchBlock, dispatch, setValue]);

  useEffect(() => {
    const block = localStorage.getItem("block");
    const floor = localStorage.getItem("floor");
    if (block && floor) {
      dispatch(fetchWards(block, floor));
    }
  }, [watchBlock, watchFloor, dispatch]);

  useEffect(() => {
    if (transferPatientState.success) {
      setDialogProps({
        title: "Success",
        message: "Patient transfer request has been successfully submitted.",
        type: "success",
      });
      setDialogOpen(true);
      dispatch(resetCurrentTransferPatient());
    } else if (transferPatientState.error) {
      setDialogProps({
        title: "Error",
        message: "Failed to submit patient transfer request. Please try again.",
        type: "error",
      });
      setDialogOpen(true);
      dispatch(resetCurrentTransferPatient());
    }
  }, [transferPatientState, dispatch]);

  const handleCancel = () => {
    reset();
  };

  const onSubmit = (data) => {
    const formdata = {
      staff_id: Id,
      patient_id: data.patient_id,
      admission_id: data.admission_id,
      old_block_id: data.old_block_id,
      old_floor_id: data.old_floor_id,
      old_ward_id: data.old_ward_id,
      new_block_id: data.new_block_id,
      new_floor_id: data.new_floor_id,
      new_ward_id: data.new_ward_id,
      reason: data.reason,
    };

    dispatch(addTransferPatient(formdata));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogProps.type === "success") {
      reset();
    }
  };

  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = () => {
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };

  const getCurrentBlockOptions = () => {
    if (blockList?.Blocks?.data) {
      return blockList.Blocks.data.map((block) => ({
        value: block.id,
        label: block.block_name,
      }));
    } else if (blockList?.blocks?.data) {
      return blockList.blocks.data.map((block) => ({
        value: block.id,
        label: block.block_name,
      }));
    }
    return [];
  };

  const getCurrentFloorOptions = () => {
    if (floorAllList?.floors?.data) {
      return floorAllList.floors.data.map((floor) => ({
        value: floor.id,
        label: floor.floor_name,
      }));
    }
    return [];
  };

  const getCurrentWardOptions = () => {
    if (wardAllList?.wards?.data) {
      return wardAllList.wards.data.map((ward) => ({
        value: ward.id,
        label: ward.ward_name,
      }));
    }
    return [];
  };

  const patientFields = [
    {
      name: "admission_id",
      label: "Select Patient",
      type: "select",
      options:
        admissionsList?.admissions?.data
          ?.filter((admission) => admission.admission_status !== 3)
          .map((admission) => ({
            value: admission.id,
            label: `${admission.Patient?.patient_name || "Unknown"} - (${
              admission.Patient?.phone || "No Phone"
            })`,
          })) || [],
      rules: { required: "Patient selection is required" },
      startIcon: <PersonIcon />,
    },
  ];

  const currentWardFields = [
    {
      name: "old_block_id",
      label: "Current Block",
      type: "select",
      options: getCurrentBlockOptions(),
      rules: { required: "Current block is required" },
      startIcon: <LocalHospitalIcon />,
      readOnly: true,
    },
    {
      name: "old_floor_id",
      label: "Current Floor",
      type: "select",
      options: getCurrentFloorOptions(),
      rules: { required: "Current floor is required" },
      startIcon: <LocalHospitalIcon />,
      readOnly: true,
    },
    {
      name: "old_ward_id",
      label: "Current Ward",
      type: "select",
      options: getCurrentWardOptions(),
      rules: { required: "Current ward is required" },
      startIcon: <LocalHospitalIcon />,
      readOnly: true,
    },
  ];

  const transferFields = [
    {
      name: "new_block_id",
      label: "New Block",
      type: "select",
      options: getBlockOptions(),
      rules: { required: "New block is required" },
      startIcon: <LocalHospitalIcon />,
    },
    {
      name: "new_floor_id",
      label: "New Floor",
      type: "select",
      options: getFloorOptions(),
      rules: { required: "Floor selection is required" },
      startIcon: <ApartmentIcon />,
      // disabled: !watchBlock,
    },
    {
      name: "new_ward_id",
      label: "New Ward",
      type: "select",
      options: getWardOptions(),
      rules: { required: "Ward type is required" },
      startIcon: <MeetingRoomIcon />,
      // disabled: !watchFloor || !watchBlock,
    },
  ];

  const reasonField = [
    {
      name: "reason",
      label: "Reason for Transfer",
      type: "text",
      multipleLine: true,
      rules: { required: "Reason for transfer is required" },
      startIcon: <NoteIcon />,
    },
  ];

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        <Typography variant="h4" sx={styles.heading}>
          Patient Transfer Form
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4 }}>
          Complete all fields to request a patient transfer
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Patient Information
              </Typography>
              <ReusableFormFields
                inputFields={patientFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12 }}
              />
              <input type="hidden" {...register("patient_id")} />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Location
              </Typography>
              <ReusableFormFields
                inputFields={currentWardFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12 }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Transfer Details
              </Typography>
              <ReusableFormFields
                inputFields={transferFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Transfer Reason
              </Typography>
              <ReusableFormFields
                inputFields={reasonField}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12 }}
              />
            </Grid>

            <Box sx={styles.buttonsContainer}>
              <CustomButton
                text="Cancel"
                variant="contained"
                size="small"
                onClick={handleCancel}
              />
              <CustomButton
                type="submit"
                text="Submit"
                variant="contained"
                size="small"
              />
            </Box>
          </Grid>
        </form>
      </Paper>

      {/* Message Dialog */}
      <MessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={dialogProps.title}
        message={dialogProps.message}
        type={dialogProps.type}
      />
    </Box>
  );
};

export default PatientTransferForm;
